import { backendApi } from "./backend.api";
import {
  IOrder,
  ISummaryItem,
  IStoreDocument,
  StatusOrder,
} from "shared/models/models";
import { getSpaceId } from "shared/utils/space";

export interface IOrderListQuery {
  date?: string;
  visit?: string;
  statusId?: StatusOrder | StatusOrder[];
  addressId?: number;
  spaceId?: number;
}

interface summaryQuery {
  date?: Date;
  selected?: number[];
}

interface prevQuery {
  id: string;
  sup?: boolean; // get parent/first order on the same date
}

interface getPrevByVisitQuery {
  id: number;
  type?: "OPENED" | "CLOSED" | "READY";
}

export const orderApi = backendApi.injectEndpoints({
  endpoints: (build) => {
    return {
      orders: build.query<IOrder[], IOrderListQuery>({
        query: (queryParams) => {
          const spaceId = getSpaceId();
          const params: IOrderListQuery = { ...queryParams };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/order",
            method: "GET",
            params: params,
          };
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Orders" as const, id })),
                "Orders",
              ]
            : ["Orders"],
      }),
      getOrderSummary: build.query<ISummaryItem[], summaryQuery>({
        query: (queryParams) => ({
          url: "/order/summary",
          method: "GET",
          params: queryParams,
        }),
      }),
      getOrder: build.query<IOrder, number>({
        query: (id) => ({ url: `/order/${id}`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "Orders", id: item?.id }],
      }),
      getOrderStoreDocument: build.query<IStoreDocument, string>({
        query: (id) => ({ url: `/order/${id}/store-doc`, method: "GET" }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (item) => [{ type: "StoreDocument", id: item?.id }],
      }),
      getNextOrder: build.query<IOrder, string>({
        query: (id) => ({ url: `/order/next/${id}`, method: "GET" }),
        providesTags: (item) => [{ type: "Orders", id: item?.id }],
      }),
      getPrevOrder: build.query<IOrder, prevQuery>({
        query: (prevQuery) => ({
          url: `/order/prev/${prevQuery.id}`,
          method: "GET",
          params: prevQuery,
        }),
        providesTags: (item) => [{ type: "Orders", id: item?.id }],
      }),
      getPrevOrderByVisit: build.query<IOrder[], getPrevByVisitQuery>({
        query: (query) => ({
          url: `/order/prev-by-visit/${query.id}`,
          method: "GET",
          params: query,
        }),
        transformErrorResponse: (response: { status: string | number }) =>
          response.status,
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "Orders" as const, id })),
                "Orders",
              ]
            : ["Orders"],
      }),
      addOrder: build.mutation<IOrder, Partial<IOrder>>({
        query: (payload) => {
          const spaceId = getSpaceId();
          const params: Partial<IOrder> = { ...payload };
          if (spaceId) {
            params.spaceId = spaceId;
          }
          return {
            url: "/order",
            method: "POST",
            data: params,
          };
        },
        invalidatesTags: ["Orders"],
        transformResponse: (response: { data: IOrder }) => response.data,
      }),
      updateOrder: build.mutation<IOrder, Partial<IOrder>>({
        query: (payload) => ({
          url: `/order/${payload.id}`,
          method: "PATCH",
          data: payload,
        }),
        invalidatesTags: (item) => [{ type: "Orders", id: item?.id }],
      }),
      deleteOrder: build.mutation<IOrder, number>({
        query: (id) => ({
          url: `/order/${id}`,
          method: "DELETE",
          credentials: "include",
        }),
        invalidatesTags: ["Orders"],
      }),
    };
  },
});

export const {
  useOrdersQuery,
  useAddOrderMutation,
  useDeleteOrderMutation,
  useGetOrderQuery,
  useUpdateOrderMutation,
  useGetPrevOrderByVisitQuery,
  useGetOrderStoreDocumentQuery,
  useGetNextOrderQuery,
  useGetPrevOrderQuery,
  useGetOrderSummaryQuery,
} = orderApi;
